import React, { useState, useEffect } from 'react';
import { TagihanSiswa, TransaksiPembayaran, Siswa, Kelas, PosTagihan, TahunAjaran } from '../../types/index.js';
import { exportToExcel, exportToPdf } from '../../utils/reportExport.js';
import { formatPaymentMethod, getPokokNominal } from '../../utils/paymentMethod.js';
import { Pagination } from '../ui/Pagination.js';
import {
  FileText,
  Printer,
  Search,
  Filter,
  Download,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Send,
  FileSpreadsheet,
  UserCheck,
  Users,
  Calendar,
  Banknote,
  CreditCard,
  BarChart2,
  List,
  GraduationCap,
  School,
} from 'lucide-react';

interface ReportViewProps {
  activeReportType: 'Report Tagihan' | 'Report Pembayaran' | 'Report Tunggakan' | 'Report Penerimaan' | 'Report Tabungan';
  tagihanList: TagihanSiswa[];
  transaksiList: TransaksiPembayaran[];
  siswaList: Siswa[];
  kelasList: Kelas[];
  posList: PosTagihan[];
  tahunAjaranList: TahunAjaran[];
  currentUser?: any;
}

export const ReportView: React.FC<ReportViewProps> = ({
  activeReportType,
  tagihanList,
  transaksiList,
  siswaList,
  kelasList,
  posList,
  tahunAjaranList,
  currentUser,
}) => {
  const activeTahun = tahunAjaranList.find((t) => t.isAktif)?.id || 'ALL';
  const [filterKelas, setFilterKelas] = useState('ALL');
  const [filterTahun, setFilterTahun] = useState(activeTahun);
  const [filterKasir, setFilterKasir] = useState('ALL');
  const [filterPeriode, setFilterPeriode] = useState<'ALL' | 'TODAY' | 'LAST_7_DAYS' | 'THIS_MONTH' | 'CUSTOM'>('ALL');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [pembayaranSubView, setPembayaranSubView] = useState<'DETAIL' | 'REKAP_KASIR'>('DETAIL');
  const [tagihanSubView, setTagihanSubView] = useState<'PER_KELAS' | 'PER_SISWA' | 'PER_WALI_KELAS' | 'DETAIL'>('PER_KELAS');
  const [search, setSearch] = useState('');
  const [pembayaranPage, setPembayaranPage] = useState(1);
  const [pembayaranPerPage, setPembayaranPerPage] = useState(50);
  const [tagihanPage, setTagihanPage] = useState(1);
  const [tagihanPerPage, setTagihanPerPage] = useState(25);
  const [tagihanSiswaPage, setTagihanSiswaPage] = useState(1);
  const [tagihanSiswaPerPage, setTagihanSiswaPerPage] = useState(25);
  const [tunggakanPage, setTunggakanPage] = useState(1);
  const [tunggakanPerPage, setTunggakanPerPage] = useState(25);
  const [tabunganPage, setTabunganPage] = useState(1);
  const [tabunganPerPage, setTabunganPerPage] = useState(25);

  useEffect(() => {
    setTagihanPage(1);
    setTagihanSiswaPage(1);
    setTunggakanPage(1);
    setPembayaranPage(1);
    setTabunganPage(1);
  }, [filterKelas, filterTahun]);

  const tahunLabel = filterTahun === 'ALL' ? 'Semua Tahun' : tahunAjaranList.find(t => t.id === filterTahun)?.tahun || filterTahun;

  // 1. Report Tagihan calculations
  const tagihanFiltered = tagihanList.filter((t) => {
    const s = siswaList.find((x) => x.id === t.siswaId);
    const matchKelas = filterKelas === 'ALL' || (s && s.kelasNama === filterKelas);
    const matchTahun = filterTahun === 'ALL' || t.tahunAjaran === filterTahun;
    return matchKelas && matchTahun;
  });

  const totalNilaiTagihan = tagihanFiltered.reduce((sum, t) => sum + t.total, 0);
  const totalTerbayarTagihan = tagihanFiltered.reduce((sum, t) => sum + t.bayar, 0);
  const totalSisaTunggakan = tagihanFiltered.reduce((sum, t) => sum + t.sisa, 0);

  // Rekapitulasi Tagihan & Pembayaran per Kelas
  const rekapTagihanPerKelas = React.useMemo(() => {
    const map = new Map<string, {
      kelasNama: string;
      tingkat: number;
      waliKelas: string;
      jumlahSiswa: number;
      totalTagihan: number;
      totalTerbayar: number;
      totalSisa: number;
      persenLunas: number;
    }>();

    kelasList.forEach((k) => {
      if (filterKelas === 'ALL' || k.nama === filterKelas) {
        const jml = siswaList.filter((s) => s.kelasNama === k.nama).length;
        map.set(k.nama, {
          kelasNama: k.nama,
          tingkat: k.tingkat || 0,
          waliKelas: k.waliKelas || 'Belum Ditentukan',
          jumlahSiswa: jml,
          totalTagihan: 0,
          totalTerbayar: 0,
          totalSisa: 0,
          persenLunas: 0,
        });
      }
    });

    tagihanFiltered.forEach((t) => {
      const s = siswaList.find((x) => x.id === t.siswaId);
      const kNama = (s && s.kelasNama) || t.kelas || t.kelasNama || 'Lainnya';
      if (!map.has(kNama)) {
        const kObj = kelasList.find((k) => k.nama === kNama);
        map.set(kNama, {
          kelasNama: kNama,
          tingkat: kObj?.tingkat || 0,
          waliKelas: kObj?.waliKelas || 'Belum Ditentukan',
          jumlahSiswa: siswaList.filter((x) => x.kelasNama === kNama).length,
          totalTagihan: 0,
          totalTerbayar: 0,
          totalSisa: 0,
          persenLunas: 0,
        });
      }
      const item = map.get(kNama)!;
      item.totalTagihan += t.total;
      item.totalTerbayar += t.bayar;
      item.totalSisa += t.sisa;
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        persenLunas: item.totalTagihan > 0 ? Math.round((item.totalTerbayar / item.totalTagihan) * 100) : 0,
      }))
      .filter((item) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return item.kelasNama.toLowerCase().includes(q) || item.waliKelas.toLowerCase().includes(q);
      })
      .sort((a, b) => a.kelasNama.localeCompare(b.kelasNama));
  }, [kelasList, tagihanFiltered, siswaList, filterKelas, search]);

  // Rekapitulasi Tagihan & Pembayaran per Siswa (Lengkap Terbayar & Belum Bayar)
  const rekapTagihanPerSiswa = React.useMemo(() => {
    const map = new Map<string, {
      siswaId: string;
      nis: string;
      namaSiswa: string;
      kelasNama: string;
      jumlahTagihan: number;
      totalTagihan: number;
      totalTerbayar: number;
      totalSisa: number;
      persenLunas: number;
      status: 'LUNAS' | 'SEBAGIAN' | 'BELUM_BAYAR';
    }>();

    tagihanFiltered.forEach((t) => {
      const s = siswaList.find((x) => x.id === t.siswaId);
      const sId = t.siswaId || s?.id || t.nis;
      if (!map.has(sId)) {
        map.set(sId, {
          siswaId: sId,
          nis: t.nis || s?.nis || '-',
          namaSiswa: t.namaSiswa || s?.nama || '-',
          kelasNama: (s && s.kelasNama) || t.kelas || t.kelasNama || '-',
          jumlahTagihan: 0,
          totalTagihan: 0,
          totalTerbayar: 0,
          totalSisa: 0,
          persenLunas: 0,
          status: 'BELUM_BAYAR',
        });
      }
      const rec = map.get(sId)!;
      rec.jumlahTagihan += 1;
      rec.totalTagihan += t.total;
      rec.totalTerbayar += t.bayar;
      rec.totalSisa += t.sisa;
    });

    return Array.from(map.values())
      .map((r) => {
        let status: 'LUNAS' | 'SEBAGIAN' | 'BELUM_BAYAR' = 'BELUM_BAYAR';
        if (r.totalSisa <= 0 && r.totalTagihan > 0) status = 'LUNAS';
        else if (r.totalTerbayar > 0 && r.totalSisa > 0) status = 'SEBAGIAN';
        const persenLunas = r.totalTagihan > 0 ? Math.round((r.totalTerbayar / r.totalTagihan) * 100) : 0;
        return { ...r, status, persenLunas };
      })
      .filter((r) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          r.namaSiswa.toLowerCase().includes(q) ||
          r.nis.toLowerCase().includes(q) ||
          r.kelasNama.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.namaSiswa.localeCompare(b.namaSiswa));
  }, [tagihanFiltered, siswaList, search]);

  // Rekapitulasi Tagihan & Pembayaran per Wali Kelas
  const rekapTagihanPerWaliKelas = React.useMemo(() => {
    const waliMap = new Map<string, {
      waliKelas: string;
      kelasList: string[];
      totalSiswa: number;
      totalTagihan: number;
      totalTerbayar: number;
      totalSisa: number;
      persenLunas: number;
    }>();

    rekapTagihanPerKelas.forEach((k) => {
      const w = k.waliKelas && k.waliKelas.trim() !== '' ? k.waliKelas : 'Belum Ditentukan';
      if (!waliMap.has(w)) {
        waliMap.set(w, {
          waliKelas: w,
          kelasList: [],
          totalSiswa: 0,
          totalTagihan: 0,
          totalTerbayar: 0,
          totalSisa: 0,
          persenLunas: 0,
        });
      }
      const entry = waliMap.get(w)!;
      if (!entry.kelasList.includes(k.kelasNama)) {
        entry.kelasList.push(k.kelasNama);
      }
      entry.totalSiswa += k.jumlahSiswa;
      entry.totalTagihan += k.totalTagihan;
      entry.totalTerbayar += k.totalTerbayar;
      entry.totalSisa += k.totalSisa;
    });

    return Array.from(waliMap.values())
      .map((w) => ({
        ...w,
        persenLunas: w.totalTagihan > 0 ? Math.round((w.totalTerbayar / w.totalTagihan) * 100) : 0,
      }))
      .filter((w) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          w.waliKelas.toLowerCase().includes(q) ||
          w.kelasList.some((k) => k.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => a.waliKelas.localeCompare(b.waliKelas));
  }, [rekapTagihanPerKelas, search]);

  // Pagination untuk Rekap Per Siswa
  const totalSiswaRekapRows = rekapTagihanPerSiswa.length;
  const totalSiswaRekapPages = Math.ceil(totalSiswaRekapRows / tagihanSiswaPerPage) || 1;
  const paginatedRekapSiswa = rekapTagihanPerSiswa.slice(
    (tagihanSiswaPage - 1) * tagihanSiswaPerPage,
    tagihanSiswaPage * tagihanSiswaPerPage
  );

  // 2. Report Tunggakan (Only unpaid bills)
  const tunggakanList = tagihanFiltered.filter((t) => t.status !== 'PAID');

  // Unique Cashiers / Petugas list
  const uniqueKasirList = React.useMemo(() => {
    const set = new Set<string>();
    transaksiList.forEach((t) => {
      if (t.kasir) set.add(t.kasir);
    });
    return Array.from(set).sort();
  }, [transaksiList]);

  // Helper date matcher
  const isMatchDate = (dateStr: string) => {
    if (filterPeriode === 'ALL') return true;
    const d = new Date(dateStr);
    const now = new Date();
    if (filterPeriode === 'TODAY') {
      return d.toDateString() === now.toDateString();
    }
    if (filterPeriode === 'LAST_7_DAYS') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return d >= sevenDaysAgo && d <= now;
    }
    if (filterPeriode === 'THIS_MONTH') {
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
    if (filterPeriode === 'CUSTOM') {
      if (customStartDate && new Date(dateStr) < new Date(customStartDate + 'T00:00:00')) return false;
      if (customEndDate && new Date(dateStr) > new Date(customEndDate + 'T23:59:59')) return false;
      return true;
    }
    return true;
  };

  // 3. Report Pembayaran with kasir, date, and search filtering
  const pembayaranFiltered = transaksiList.filter((trx) => {
    if (trx.status !== 'SUCCESS') return false;
    if (filterKasir !== 'ALL' && trx.kasir !== filterKasir) return false;
    if (!isMatchDate(trx.tanggal)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchNo = trx.noTransaksi?.toLowerCase().includes(q);
      const matchKwt = trx.noKuitansi?.toLowerCase().includes(q);
      const matchSiswa = trx.namaSiswa?.toLowerCase().includes(q);
      const matchKasir = trx.kasir?.toLowerCase().includes(q);
      const matchNis = trx.nis?.toLowerCase().includes(q);
      if (!matchNo && !matchKwt && !matchSiswa && !matchKasir && !matchNis) return false;
    }
    return true;
  });

  const totalPenerimaanKas = pembayaranFiltered.reduce((sum, trx) => sum + getPokokNominal(trx), 0);
  const totalPenerimaanTunai = pembayaranFiltered
    .filter((trx) => (trx.metodeBayar as any) === 'CASH' || (trx.metodeBayar as any) === 'MANUAL_CASH' || trx.channel === 'CASH')
    .reduce((sum, trx) => sum + getPokokNominal(trx), 0);
  const totalPenerimaanNonTunai = totalPenerimaanKas - totalPenerimaanTunai;

  // Rekapitulasi per Petugas Kasir Loket
  const rekapKasirList = React.useMemo(() => {
    const map = new Map<string, {
      kasir: string;
      totalTransaksi: number;
      totalNominal: number;
      totalTunai: number;
      totalNonTunai: number;
      transaksiTerakhir?: string;
    }>();

    // Loop through all success transactions matching date filter (or all)
    transaksiList
      .filter((trx) => trx.status === 'SUCCESS' && isMatchDate(trx.tanggal))
      .forEach((trx) => {
        const k = trx.kasir || 'SISTEM';
        const existing = map.get(k) || {
          kasir: k,
          totalTransaksi: 0,
          totalNominal: 0,
          totalTunai: 0,
          totalNonTunai: 0,
          transaksiTerakhir: trx.tanggal,
        };
        const pokok = getPokokNominal(trx);
        existing.totalTransaksi += 1;
        existing.totalNominal += pokok;
        const isTunai = (trx.metodeBayar as any) === 'CASH' || (trx.metodeBayar as any) === 'MANUAL_CASH' || trx.channel === 'CASH';
        if (isTunai) existing.totalTunai += pokok;
        else existing.totalNonTunai += pokok;
        map.set(k, existing);
      });

    return Array.from(map.values()).sort((a, b) => b.totalNominal - a.totalNominal);
  }, [transaksiList, filterPeriode, customStartDate, customEndDate]);

  const totalPembayaranPages = Math.ceil(pembayaranFiltered.length / pembayaranPerPage) || 1;
  const paginatedPembayaran = pembayaranFiltered.slice(
    (pembayaranPage - 1) * pembayaranPerPage,
    pembayaranPage * pembayaranPerPage
  );

  const totalTagihanRows = tagihanFiltered.length;
  const totalTagihanPages = Math.ceil(totalTagihanRows / tagihanPerPage) || 1;
  const paginatedTagihan = tagihanFiltered.slice(
    (tagihanPage - 1) * tagihanPerPage,
    tagihanPage * tagihanPerPage
  );

  const totalTunggakanRows = tunggakanList.length;
  const totalTunggakanPages = Math.ceil(totalTunggakanRows / tunggakanPerPage) || 1;
  const paginatedTunggakan = tunggakanList.slice(
    (tunggakanPage - 1) * tunggakanPerPage,
    tunggakanPage * tunggakanPerPage
  );

  const tabunganFiltered = siswaList.filter(s => filterKelas === 'ALL' || s.kelasNama === filterKelas);
  const totalTabunganRows = tabunganFiltered.length;
  const totalTabunganPages = Math.ceil(totalTabunganRows / tabunganPerPage) || 1;
  const paginatedTabungan = tabunganFiltered.slice(
    (tabunganPage - 1) * tabunganPerPage,
    tabunganPage * tabunganPerPage
  );

  // Helper for Exporting Current Active Report to Excel (.csv format with UTF-8 BOM)
  const handleExportExcel = () => {
    if (activeReportType === 'Report Tagihan') {
      if (tagihanSubView === 'PER_KELAS') {
        const grandTarget = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTagihan, 0);
        const grandBayar = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalSisa, 0);
        const grandPersen = grandTarget > 0 ? Math.round((grandBayar / grandTarget) * 100) : 0;

        exportToExcel({
          title: 'Rekapitulasi Pembayaran Siswa per Kelas',
          subtitle: `Tahun Ajaran ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'Rombel / Kelas', key: 'kelasNama', align: 'left' },
            { header: 'Wali Kelas', key: 'waliKelas', align: 'left' },
            { header: 'Jml Siswa', key: 'jumlahSiswa', align: 'center' },
            { header: 'Target Tagihan (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Realisasi Pembayaran (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Sisa Belum Terbayar (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Capaian (%)', key: 'persenDisplay', align: 'center' },
          ],
          data: rekapTagihanPerKelas.map(k => ({
            ...k,
            persenDisplay: `${k.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Target Penagihan Semua Kelas', value: grandTarget, isCurrency: true },
            { label: 'Total Realisasi Kas Masuk (Lunas)', value: grandBayar, isCurrency: true },
            { label: 'Total Sisa Piutang Tertunggak', value: grandSisa, isCurrency: true },
            { label: 'Rata-rata Persentase Kelunasan', value: `${grandPersen}%` },
          ],
        });
      } else if (tagihanSubView === 'PER_SISWA') {
        const grandTarget = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalTagihan, 0);
        const grandBayar = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalSisa, 0);

        exportToExcel({
          title: 'Rekapitulasi Laporan Pembayaran per Siswa',
          subtitle: `Rincian Terbayarkan & Tagihan Belum Terbayar Siswa T.A ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa', align: 'left' },
            { header: 'Kelas', key: 'kelasNama', align: 'center' },
            { header: 'Jml Tagihan', key: 'jumlahTagihan', align: 'center' },
            { header: 'Total Tagihan (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Total Terbayarkan (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Tagihan Belum Terbayar (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Capaian (%)', key: 'persenDisplay', align: 'center' },
            { header: 'Status Kelunasan', key: 'status', align: 'center' },
          ],
          data: rekapTagihanPerSiswa.map(s => ({
            ...s,
            persenDisplay: `${s.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Siswa Terdaftar', value: rekapTagihanPerSiswa.length },
            { label: 'Total Kewajiban Tagihan', value: grandTarget, isCurrency: true },
            { label: 'Total Pembayaran Diterima (Lunas)', value: grandBayar, isCurrency: true },
            { label: 'Total Sisa Piutang Belum Terbayar', value: grandSisa, isCurrency: true },
          ],
        });
      } else if (tagihanSubView === 'PER_WALI_KELAS') {
        const grandTarget = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTagihan, 0);
        const grandBayar = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalSisa, 0);
        const grandPersen = grandTarget > 0 ? Math.round((grandBayar / grandTarget) * 100) : 0;

        exportToExcel({
          title: 'Rekapitulasi Pembayaran Siswa per Wali Kelas',
          subtitle: `Laporan Monitoring Kinerja Pembayaran per Wali Kelas T.A ${tahunLabel}`,
          filterInfo: `T.A: ${tahunLabel}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'Nama Wali Kelas', key: 'waliKelas', align: 'left' },
            { header: 'Rombel / Kelas', key: 'kelasDisplay', align: 'left' },
            { header: 'Total Siswa', key: 'totalSiswa', align: 'center' },
            { header: 'Target Tagihan (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Realisasi Pembayaran (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Sisa Belum Terbayar (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Ketercapaian (%)', key: 'persenDisplay', align: 'center' },
          ],
          data: rekapTagihanPerWaliKelas.map(w => ({
            ...w,
            kelasDisplay: w.kelasList.join(', '),
            persenDisplay: `${w.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Wali Kelas Bertugas', value: rekapTagihanPerWaliKelas.length },
            { label: 'Grand Total Target Penagihan', value: grandTarget, isCurrency: true },
            { label: 'Grand Total Realisasi Pembayaran', value: grandBayar, isCurrency: true },
            { label: 'Grand Total Sisa Piutang Kelas', value: grandSisa, isCurrency: true },
            { label: 'Rata-rata Ketercapaian Sekolah', value: `${grandPersen}%` },
          ],
        });
      } else {
        exportToExcel({
          title: 'Laporan Rincian Item Tagihan Siswa',
          subtitle: `Tahun Ajaran ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa' },
            { header: 'Kelas', key: 'kelas', align: 'center' },
            { header: 'Pos Tagihan', key: 'namaPos' },
            { header: 'Bulan', key: 'bulan' },
            { header: 'Tarif (Rp)', key: 'total', align: 'right', isCurrency: true },
            { header: 'Potongan (Rp)', key: 'potongan', align: 'right', isCurrency: true },
            { header: 'Bayar (Rp)', key: 'bayar', align: 'right', isCurrency: true },
            { header: 'Sisa Tunggakan (Rp)', key: 'sisa', align: 'right', isCurrency: true },
            { header: 'Status', key: 'status', align: 'center' },
          ],
          data: tagihanFiltered.map((t) => ({
            ...t,
            kelas: t.kelas || t.kelasNama || '-',
            bulan: t.bulan || '-',
          })),
          summaryRows: [
            { label: 'Total Kewajiban Tagihan', value: totalNilaiTagihan, isCurrency: true },
            { label: 'Total Terbayar (Kas Masuk)', value: totalTerbayarTagihan, isCurrency: true },
            { label: 'Total Sisa Tunggakan', value: totalSisaTunggakan, isCurrency: true },
          ],
        });
      }
    } else if (activeReportType === 'Report Pembayaran') {
      if (pembayaranSubView === 'REKAP_KASIR') {
        const grandTotalRekap = rekapKasirList.reduce((sum, r) => sum + r.totalNominal, 0);
        const grandTunaiRekap = rekapKasirList.reduce((sum, r) => sum + r.totalTunai, 0);
        const grandNonTunaiRekap = rekapKasirList.reduce((sum, r) => sum + r.totalNonTunai, 0);
        const grandTrxRekap = rekapKasirList.reduce((sum, r) => sum + r.totalTransaksi, 0);

        exportToExcel({
          title: 'Rekapitulasi Penerimaan Kas per Petugas Kasir Loket',
          subtitle: 'Rekap Pembayaran Tunai & Non-Tunai Berdasarkan Petugas Kasir Loket',
          filterInfo: `Periode: ${filterPeriode} | Petugas: ${filterKasir}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'Nama Petugas Kasir Loket', key: 'kasir' },
            { header: 'Total Transaksi', key: 'totalTransaksi', align: 'center' },
            { header: 'Penerimaan Tunai / Cash (Rp)', key: 'totalTunai', align: 'right', isCurrency: true },
            { header: 'Penerimaan Non-Tunai / Gateway (Rp)', key: 'totalNonTunai', align: 'right', isCurrency: true },
            { header: 'Total Penerimaan (Rp)', key: 'totalNominal', align: 'right', isCurrency: true },
          ],
          data: rekapKasirList,
          summaryRows: [
            { label: 'Total Transaksi Semua Kasir', value: grandTrxRekap },
            { label: 'Total Kas Masuk Tunai (Fisik di Kasir)', value: grandTunaiRekap, isCurrency: true },
            { label: 'Total Kas Masuk Non-Tunai / Gateway', value: grandNonTunaiRekap, isCurrency: true },
            { label: 'Grand Total Penerimaan Kas', value: grandTotalRekap, isCurrency: true },
          ],
        });
      } else {
        exportToExcel({
          title: 'Laporan Riwayat Pembayaran Siswa',
          subtitle: 'Mutasi Transaksi Lunas Kasir & Payment Gateway',
          filterInfo: `Status: LUNAS | Kasir: ${filterKasir} | Periode: ${filterPeriode}`,
          columns: [
            { header: 'No', key: '_index', align: 'center' },
            { header: 'Tanggal & Waktu', key: 'tanggal' },
            { header: 'No. Transaksi', key: 'noTransaksi' },
            { header: 'No. Kuitansi', key: 'noKuitansi' },
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa' },
            { header: 'Kelas', key: 'kelas', align: 'center' },
            { header: 'Total Bayar (Rp)', key: 'totalBayar', align: 'right', isCurrency: true },
            { header: 'Channel / Metode', key: 'channel' },
            { header: 'Kasir / Petugas', key: 'kasir' },
          ],
          data: pembayaranFiltered.map((trx) => ({
            ...trx,
            channel: formatPaymentMethod(trx.channel, trx.metodeBayar),
            totalBayar: getPokokNominal(trx),
          })),
          summaryRows: [
            { label: 'Total Transaksi Lunas', value: pembayaranFiltered.length },
            { label: 'Total Penerimaan Kas', value: totalPenerimaanKas, isCurrency: true },
            { label: 'Uang Tunai (Cash di Kasir)', value: totalPenerimaanTunai, isCurrency: true },
            { label: 'Non-Tunai (Gateway / VA / QRIS)', value: totalPenerimaanNonTunai, isCurrency: true },
          ],
        });
      }
    } else if (activeReportType === 'Report Tunggakan') {
      exportToExcel({
        title: 'Laporan Tunggakan Siswa Belum Lunas',
        subtitle: `Daftar Piutang Siswa Tahun Ajaran ${tahunLabel}`,
        filterInfo: `Kelas: ${filterKelas} | Status: BELUM LUNAS`,
        columns: [
          { header: 'No', key: '_index', align: 'center' },
          { header: 'NIS', key: 'nis', align: 'center' },
          { header: 'Nama Siswa', key: 'namaSiswa' },
          { header: 'Kelas', key: 'kelas', align: 'center' },
          { header: 'Pos Tagihan', key: 'namaPos' },
          { header: 'Bulan / Periode', key: 'bulan' },
          { header: 'Tarif Asli (Rp)', key: 'total', align: 'right', isCurrency: true },
          { header: 'Sisa Piutang (Rp)', key: 'sisa', align: 'right', isCurrency: true },
        ],
        data: tunggakanList.map((t) => ({
          ...t,
          kelas: t.kelas || t.kelasNama || '-',
          bulan: t.bulan || '-',
        })),
        summaryRows: [
          { label: 'Total Item Tagihan Nunggak', value: tunggakanList.length },
          { label: 'Total Piutang Belum Tertagih', value: totalSisaTunggakan, isCurrency: true },
        ],
      });
    } else if (activeReportType === 'Report Penerimaan') {
      const penerimaanData = posList.map((pos) => {
        const total = tagihanList.filter((t) => t.posId === pos.id).reduce((sum, t) => sum + t.bayar, 0);
        return {
          kode: pos.kode,
          namaPos: pos.namaPos,
          akunPenerimaan: pos.akunPenerimaan,
          totalBayar: total,
        };
      });
      const grandTotal = penerimaanData.reduce((acc, curr) => acc + curr.totalBayar, 0);

      exportToExcel({
        title: 'Rekapitulasi Penerimaan Kas per Pos Tagihan',
        subtitle: `Pemisahan Kas Berdasarkan Chart of Accounts (COA) T.A ${tahunLabel}`,
        filterInfo: `Semua Pos Tagihan Terdaftar (${posList.length} Pos)`,
        columns: [
          { header: 'No', key: '_index', align: 'center' },
          { header: 'Kode Pos', key: 'kode', align: 'center' },
          { header: 'Nama Pos Tagihan', key: 'namaPos' },
          { header: 'Akun Penerimaan (COA)', key: 'akunPenerimaan' },
          { header: 'Total Diterima (Rp)', key: 'totalBayar', align: 'right', isCurrency: true },
        ],
        data: penerimaanData,
        summaryRows: [{ label: 'Grand Total Penerimaan Kas', value: grandTotal, isCurrency: true }],
      });
    } else if (activeReportType === 'Report Tabungan') {
      const tabunganData = siswaList
        .filter(s => filterKelas === 'ALL' || s.kelasNama === filterKelas)
        .map(s => ({
          nis: s.nis,
          nama: s.nama,
          kelasNama: s.kelasNama,
          saldo: s.saldoTabungan || 0
        }));
      
      const totalSaldo = tabunganData.reduce((sum, s) => sum + s.saldo, 0);
      
      exportToExcel({
        title: 'Rekapitulasi Saldo Tabungan Siswa',
        subtitle: `Daftar Saldo Tabungan Aktif`,
        filterInfo: `Kelas: ${filterKelas}`,
        columns: [
          { header: 'No', key: '_index', align: 'center' },
          { header: 'NIS', key: 'nis', align: 'center' },
          { header: 'Nama Siswa', key: 'nama' },
          { header: 'Kelas', key: 'kelasNama', align: 'center' },
          { header: 'Saldo Akhir (Rp)', key: 'saldo', align: 'right', isCurrency: true },
        ],
        data: tabunganData,
        summaryRows: [{ label: 'Total Saldo Keseluruhan', value: totalSaldo, isCurrency: true }],
      });
    }
  };

  // Helper for Exporting / Printing to Formatted Printable PDF Document
  const handleExportPdf = () => {
    if (activeReportType === 'Report Tagihan') {
      if (tagihanSubView === 'PER_KELAS') {
        const grandTarget = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTagihan, 0);
        const grandBayar = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalSisa, 0);
        const grandPersen = grandTarget > 0 ? Math.round((grandBayar / grandTarget) * 100) : 0;

        exportToPdf({
          title: 'Rekapitulasi Pembayaran Siswa per Kelas',
          subtitle: `Tahun Ajaran ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'Kelas / Rombel', key: 'kelasNama', align: 'left' },
            { header: 'Wali Kelas', key: 'waliKelas', align: 'left' },
            { header: 'Siswa', key: 'jumlahSiswa', align: 'center' },
            { header: 'Target (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Realisasi (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Sisa Piutang (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Capaian', key: 'persenDisplay', align: 'center' },
          ],
          data: rekapTagihanPerKelas.map(k => ({
            ...k,
            persenDisplay: `${k.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Target Penagihan Semua Kelas', value: grandTarget, isCurrency: true },
            { label: 'Total Realisasi Kas Masuk (Lunas)', value: grandBayar, isCurrency: true },
            { label: 'Total Sisa Piutang Tertunggak', value: grandSisa, isCurrency: true },
            { label: 'Rata-rata Persentase Kelunasan', value: `${grandPersen}%` },
          ],
        });
      } else if (tagihanSubView === 'PER_SISWA') {
        const grandTarget = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalTagihan, 0);
        const grandBayar = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerSiswa.reduce((sum, s) => sum + s.totalSisa, 0);

        exportToPdf({
          title: 'Rekapitulasi Laporan Pembayaran per Siswa',
          subtitle: `Rincian Terbayarkan & Tagihan Belum Terbayar Siswa T.A ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa', align: 'left' },
            { header: 'Kelas', key: 'kelasNama', align: 'center' },
            { header: 'Total (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Terbayar (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Belum Bayar (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Status', key: 'status', align: 'center' },
          ],
          data: rekapTagihanPerSiswa.map(s => ({
            ...s,
            persenDisplay: `${s.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Siswa Terdaftar', value: rekapTagihanPerSiswa.length },
            { label: 'Total Kewajiban Tagihan', value: grandTarget, isCurrency: true },
            { label: 'Total Pembayaran Diterima (Lunas)', value: grandBayar, isCurrency: true },
            { label: 'Total Sisa Piutang Belum Terbayar', value: grandSisa, isCurrency: true },
          ],
        });
      } else if (tagihanSubView === 'PER_WALI_KELAS') {
        const grandTarget = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTagihan, 0);
        const grandBayar = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTerbayar, 0);
        const grandSisa = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalSisa, 0);
        const grandPersen = grandTarget > 0 ? Math.round((grandBayar / grandTarget) * 100) : 0;

        exportToPdf({
          title: 'Rekapitulasi Pembayaran Siswa per Wali Kelas',
          subtitle: `Laporan Monitoring Kinerja Pembayaran per Wali Kelas T.A ${tahunLabel}`,
          filterInfo: `T.A: ${tahunLabel}`,
          columns: [
            { header: 'Nama Wali Kelas', key: 'waliKelas', align: 'left' },
            { header: 'Rombel', key: 'kelasDisplay', align: 'left' },
            { header: 'Siswa', key: 'totalSiswa', align: 'center' },
            { header: 'Target (Rp)', key: 'totalTagihan', align: 'right', isCurrency: true },
            { header: 'Realisasi (Rp)', key: 'totalTerbayar', align: 'right', isCurrency: true },
            { header: 'Sisa (Rp)', key: 'totalSisa', align: 'right', isCurrency: true },
            { header: 'Capaian', key: 'persenDisplay', align: 'center' },
          ],
          data: rekapTagihanPerWaliKelas.map(w => ({
            ...w,
            kelasDisplay: w.kelasList.join(', '),
            persenDisplay: `${w.persenLunas}%`,
          })),
          summaryRows: [
            { label: 'Total Wali Kelas Bertugas', value: rekapTagihanPerWaliKelas.length },
            { label: 'Grand Total Target Penagihan', value: grandTarget, isCurrency: true },
            { label: 'Grand Total Realisasi Pembayaran', value: grandBayar, isCurrency: true },
            { label: 'Grand Total Sisa Piutang Kelas', value: grandSisa, isCurrency: true },
            { label: 'Rata-rata Ketercapaian Sekolah', value: `${grandPersen}%` },
          ],
        });
      } else {
        exportToPdf({
          title: 'Laporan Rincian Item Tagihan Siswa',
          subtitle: `Tahun Ajaran ${tahunLabel}`,
          filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
          columns: [
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa', align: 'left' },
            { header: 'Kelas', key: 'kelas', align: 'center' },
            { header: 'Pos Tagihan', key: 'namaPos', align: 'left' },
            { header: 'Bulan', key: 'bulan', align: 'center' },
            { header: 'Tarif', key: 'total', align: 'right', isCurrency: true },
            { header: 'Bayar', key: 'bayar', align: 'right', isCurrency: true },
            { header: 'Sisa', key: 'sisa', align: 'right', isCurrency: true },
            { header: 'Status', key: 'status', align: 'center' },
          ],
          data: tagihanFiltered.map((t) => ({
            ...t,
            kelas: t.kelas || t.kelasNama || '-',
            bulan: t.bulan || '-',
          })),
          summaryRows: [
            { label: 'Total Kewajiban Tagihan', value: totalNilaiTagihan, isCurrency: true },
            { label: 'Total Terbayar (Kas Masuk)', value: totalTerbayarTagihan, isCurrency: true },
            { label: 'Total Sisa Tunggakan', value: totalSisaTunggakan, isCurrency: true },
          ],
        });
      }
    } else if (activeReportType === 'Report Pembayaran') {
      if (pembayaranSubView === 'REKAP_KASIR') {
        const grandTotalRekap = rekapKasirList.reduce((sum, r) => sum + r.totalNominal, 0);
        const grandTunaiRekap = rekapKasirList.reduce((sum, r) => sum + r.totalTunai, 0);
        const grandNonTunaiRekap = rekapKasirList.reduce((sum, r) => sum + r.totalNonTunai, 0);
        const grandTrxRekap = rekapKasirList.reduce((sum, r) => sum + r.totalTransaksi, 0);

        exportToPdf({
          title: 'Rekapitulasi Penerimaan Kas per Petugas Kasir Loket',
          subtitle: 'Rekap Kas Masuk Tunai & Non-Tunai Berdasarkan Petugas Kasir',
          filterInfo: `Petugas Kasir: ${filterKasir} | Periode: ${filterPeriode}`,
          columns: [
            { header: 'Petugas Kasir', key: 'kasir', align: 'left' },
            { header: 'Jml Trx', key: 'totalTransaksi', align: 'center' },
            { header: 'Kas Tunai (Rp)', key: 'totalTunai', align: 'right', isCurrency: true },
            { header: 'Non-Tunai (Rp)', key: 'totalNonTunai', align: 'right', isCurrency: true },
            { header: 'Total Kas (Rp)', key: 'totalNominal', align: 'right', isCurrency: true },
          ],
          data: rekapKasirList,
          summaryRows: [
            { label: 'Total Transaksi Semua Kasir', value: grandTrxRekap },
            { label: 'Total Kas Fisik Tunai di Kasir', value: grandTunaiRekap, isCurrency: true },
            { label: 'Total Non-Tunai / Gateway', value: grandNonTunaiRekap, isCurrency: true },
            { label: 'Grand Total Penerimaan Kas', value: grandTotalRekap, isCurrency: true },
          ],
        });
      } else {
        exportToPdf({
          title: 'Laporan Pembayaran Siswa',
          subtitle: 'Rekapitulasi Penerimaan Kas Loket & Payment Gateway',
          filterInfo: `Status: Lunas | Kasir: ${filterKasir} | Periode: ${filterPeriode}`,
          columns: [
            { header: 'Tanggal', key: 'tanggal', align: 'left' },
            { header: 'No. Transaksi', key: 'noTransaksi', align: 'left' },
            { header: 'No. Kuitansi', key: 'noKuitansi', align: 'left' },
            { header: 'NIS', key: 'nis', align: 'center' },
            { header: 'Nama Siswa', key: 'namaSiswa', align: 'left' },
            { header: 'Kelas', key: 'kelas', align: 'center' },
            { header: 'Total (Rp)', key: 'totalBayar', align: 'right', isCurrency: true },
            { header: 'Kanal', key: 'channel', align: 'left' },
            { header: 'Petugas', key: 'kasir', align: 'left' },
          ],
          data: pembayaranFiltered.map((trx) => ({
            ...trx,
            channel: formatPaymentMethod(trx.channel, trx.metodeBayar),
            totalBayar: getPokokNominal(trx),
          })),
          summaryRows: [
            { label: 'Jumlah Transaksi', value: pembayaranFiltered.length },
            { label: 'Total Penerimaan Kas', value: totalPenerimaanKas, isCurrency: true },
            { label: 'Uang Tunai (Cash)', value: totalPenerimaanTunai, isCurrency: true },
            { label: 'Non-Tunai (Gateway)', value: totalPenerimaanNonTunai, isCurrency: true },
          ],
        });
      }
    } else if (activeReportType === 'Report Tunggakan') {
      exportToPdf({
        title: 'Laporan Tunggakan Siswa Belum Lunas',
        subtitle: `Daftar Piutang Tagihan Siswa SDMT Ponorogo`,
        filterInfo: `Kelas: ${filterKelas} | T.A: ${tahunLabel}`,
        columns: [
          { header: 'NIS', key: 'nis', align: 'center' },
          { header: 'Nama Siswa', key: 'namaSiswa', align: 'left' },
          { header: 'Kelas', key: 'kelas', align: 'center' },
          { header: 'Pos Tagihan', key: 'namaPos', align: 'left' },
          { header: 'Bulan / Periode', key: 'bulan', align: 'center' },
          { header: 'Tarif Asli', key: 'total', align: 'right', isCurrency: true },
          { header: 'Sisa Piutang', key: 'sisa', align: 'right', isCurrency: true },
        ],
        data: tunggakanList.map((t) => ({
          ...t,
          kelas: t.kelas || t.kelasNama || '-',
          bulan: t.bulan || '-',
        })),
        summaryRows: [
          { label: 'Total Item Tunggakan', value: tunggakanList.length },
          { label: 'Total Sisa Piutang', value: totalSisaTunggakan, isCurrency: true },
        ],
      });
    } else if (activeReportType === 'Report Penerimaan') {
      const penerimaanData = posList.map((pos) => {
        const total = tagihanList.filter((t) => t.posId === pos.id).reduce((sum, t) => sum + t.bayar, 0);
        return {
          kode: pos.kode,
          namaPos: pos.namaPos,
          akunPenerimaan: pos.akunPenerimaan,
          totalBayar: total,
        };
      });
      const grandTotal = penerimaanData.reduce((acc, curr) => acc + curr.totalBayar, 0);

      exportToPdf({
        title: 'Rekapitulasi Penerimaan Kas per Pos Tagihan',
        subtitle: `Pemisahan Alokasi Buku Kas Sekolah`,
        filterInfo: `Tahun Ajaran ${tahunLabel}`,
        columns: [
          { header: 'Kode', key: 'kode', align: 'center' },
          { header: 'Nama Pos Tagihan', key: 'namaPos', align: 'left' },
          { header: 'Akun Penerimaan (COA)', key: 'akunPenerimaan', align: 'left' },
          { header: 'Total Diterima', key: 'totalBayar', align: 'right', isCurrency: true },
        ],
        data: penerimaanData,
        summaryRows: [{ label: 'Grand Total Penerimaan', value: grandTotal, isCurrency: true }],
      });
    } else if (activeReportType === 'Report Tabungan') {
      const tabunganData = siswaList
        .filter(s => filterKelas === 'ALL' || s.kelasNama === filterKelas)
        .map(s => ({
          nis: s.nis,
          nama: s.nama,
          kelasNama: s.kelasNama,
          saldo: s.saldoTabungan || 0
        }));
      
      const totalSaldo = tabunganData.reduce((sum, s) => sum + s.saldo, 0);
      
      exportToPdf({
        title: 'Laporan Saldo Tabungan Siswa',
        subtitle: `Rekapitulasi Dana Titipan Siswa`,
        filterInfo: `Kelas: ${filterKelas}`,
        columns: [
          { header: 'NIS', key: 'nis', align: 'center' },
          { header: 'Nama Siswa', key: 'nama', align: 'left' },
          { header: 'Kelas', key: 'kelasNama', align: 'center' },
          { header: 'Saldo Tabungan', key: 'saldo', align: 'right', isCurrency: true },
        ],
        data: tabunganData,
        summaryRows: [{ label: 'Total Dana Pihak Ketiga (Siswa)', value: totalSaldo, isCurrency: true }],
      });
    }
  };

  const handleSendWaReminder = (namaSiswa: string, hp: string, sisa: number) => {
    const text = `Assalamu'alaikum Wr. Wb. Bapak/Ibu Wali dari ${namaSiswa}, kami menginformasikan tagihan administrasi sekolah SDMT Ponorogo yang belum terselesaikan sebesar Rp ${sisa.toLocaleString(
      'id-ID'
    )}. Pembayaran dapat melalui transfer Virtual Account atau Loket TU. Terima kasih.`;
    window.open(`https://wa.me/${hp.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header & Filter Bar (Matching Document Pages 15-18) */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">{activeReportType}</h2>
            <p className="text-xs text-slate-500">
              Laporan akuntansi keuangan, rekapitulasi penerimaan, dan tunggakan siswa SDMT Ponorogo
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportPdf}
              className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition shadow-xs cursor-pointer"
              title="Cetak atau Simpan PDF Laporan"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Export PDF</span>
            </button>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition shadow-xs cursor-pointer"
              title="Download File Format Excel (.csv / UTF-8)"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Export Excel</span>
            </button>
            <button
              onClick={handleExportPdf}
              className="flex items-center gap-1.5 bg-sky-700 hover:bg-sky-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition shadow-xs cursor-pointer"
              title="Cetak Langsung ke Printer Fisik"
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Cetak</span>
            </button>
          </div>
        </div>

        {/* Filter controls row */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {activeReportType !== 'Report Pembayaran' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Kelas:</span>
                <select
                  value={filterKelas}
                  onChange={(e) => setFilterKelas(e.target.value)}
                  className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 bg-slate-50 font-medium"
                >
                  <option value="ALL">Semua Kelas</option>
                  {kelasList.map((k) => (
                    <option key={k.id} value={k.nama}>
                      {k.nama}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filter Petugas Kasir (Khusus Report Pembayaran) */}
            {activeReportType === 'Report Pembayaran' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <UserCheck className="w-3.5 h-3.5 text-sky-600" />
                  <span>Petugas Kasir:</span>
                </span>
                <select
                  value={filterKasir}
                  onChange={(e) => {
                    setFilterKasir(e.target.value);
                    setPembayaranPage(1);
                  }}
                  className="text-xs border border-sky-300 rounded-md px-2.5 py-1.5 bg-sky-50/60 font-semibold text-slate-800"
                >
                  <option value="ALL">Semua Petugas Kasir</option>
                  {uniqueKasirList.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filter Periode Tanggal (Khusus Report Pembayaran) */}
            {activeReportType === 'Report Pembayaran' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Periode:</span>
                </span>
                <select
                  value={filterPeriode}
                  onChange={(e) => {
                    setFilterPeriode(e.target.value as any);
                    setPembayaranPage(1);
                  }}
                  className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 bg-slate-50 font-medium"
                >
                  <option value="ALL">Semua Tanggal</option>
                  <option value="TODAY">Hari Ini (Shift Kasir)</option>
                  <option value="LAST_7_DAYS">7 Hari Terakhir</option>
                  <option value="THIS_MONTH">Bulan Ini</option>
                  <option value="CUSTOM">Rentang Tanggal Khusus</option>
                </select>
              </div>
            )}

            {activeReportType === 'Report Pembayaran' && filterPeriode === 'CUSTOM' && (
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                />
                <span className="text-xs text-slate-400">s/d</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                />
              </div>
            )}

            {activeReportType !== 'Report Pembayaran' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Tahun Ajaran:</span>
                <select
                  value={filterTahun}
                  onChange={(e) => setFilterTahun(e.target.value)}
                  className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 bg-slate-50 font-medium"
                >
                  <option value="ALL">Semua Tahun</option>
                  {tahunAjaranList.map((ta) => (
                    <option key={ta.id} value={ta.id}>
                      {ta.tahun} {ta.isAktif ? '(Aktif)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search data..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-48"
            />
          </div>
        </div>
      </div>

      {/* KPI Cards Summary */}
      {activeReportType === 'Report Pembayaran' ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <span className="text-[11px] font-semibold text-sky-700 uppercase flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" />
              <span>Total Kas Masuk</span>
            </span>
            <p className="text-lg font-mono font-bold text-sky-900 mt-1">
              Rp {totalPenerimaanKas.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-slate-500">{pembayaranFiltered.length} transaksi lunas</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-emerald-200 shadow-xs bg-emerald-50/20">
            <span className="text-[11px] font-semibold text-emerald-700 uppercase flex items-center gap-1.5">
              <Banknote className="w-3.5 h-3.5" />
              <span>Kas Tunai di Kasir</span>
            </span>
            <p className="text-lg font-mono font-bold text-emerald-700 mt-1">
              Rp {totalPenerimaanTunai.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-emerald-600">Uang fisik di laci loket</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-indigo-200 shadow-xs bg-indigo-50/20">
            <span className="text-[11px] font-semibold text-indigo-700 uppercase flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" />
              <span>Kas Non-Tunai / Gateway</span>
            </span>
            <p className="text-lg font-mono font-bold text-indigo-700 mt-1">
              Rp {totalPenerimaanNonTunai.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-indigo-600">QRIS, VA Bank & Tabungan</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-purple-200 shadow-xs bg-purple-50/20">
            <span className="text-[11px] font-semibold text-purple-700 uppercase flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              <span>Petugas Aktif</span>
            </span>
            <p className="text-lg font-mono font-bold text-purple-800 mt-1">
              {rekapKasirList.length} Petugas/Kanal
            </p>
            <span className="text-[10px] text-purple-600">Siap rekap shift</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <span className="text-[11px] font-semibold text-slate-500 uppercase">Total Kewajiban Tagihan</span>
            <p className="text-lg font-mono font-bold text-slate-900 mt-1">
              Rp {totalNilaiTagihan.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-slate-400">Tahun Ajaran {tahunLabel}</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <span className="text-[11px] font-semibold text-emerald-600 uppercase">Total Terbayar (Kas Masuk)</span>
            <p className="text-lg font-mono font-bold text-emerald-700 mt-1">
              Rp {totalTerbayarTagihan.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-emerald-600">Lunas & Cicilan Berhasil</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <span className="text-[11px] font-semibold text-rose-600 uppercase">Total Sisa Tunggakan</span>
            <p className="text-lg font-mono font-bold text-rose-600 mt-1">
              Rp {totalSisaTunggakan.toLocaleString('id-ID')}
            </p>
            <span className="text-[10px] text-rose-500">Piutang yang harus ditagihkan</span>
          </div>
        </div>
      )}

      {/* TAB 1: REPORT TAGIHAN (Document Page 15) */}
      {activeReportType === 'Report Tagihan' && (
        <div className="space-y-3">
          {/* SubView Mode Toggle Bar */}
          <div className="flex flex-wrap items-center justify-between gap-2 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => setTagihanSubView('PER_KELAS')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  tagihanSubView === 'PER_KELAS'
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <School className="w-3.5 h-3.5" />
                <span>Rekap Per Kelas ({rekapTagihanPerKelas.length})</span>
              </button>
              <button
                onClick={() => setTagihanSubView('PER_SISWA')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  tagihanSubView === 'PER_SISWA'
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                <span>Rekap Per Siswa ({rekapTagihanPerSiswa.length})</span>
              </button>
              <button
                onClick={() => setTagihanSubView('PER_WALI_KELAS')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  tagihanSubView === 'PER_WALI_KELAS'
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <UserCheck className="w-3.5 h-3.5" />
                <span>Rekap Per Wali Kelas ({rekapTagihanPerWaliKelas.length})</span>
              </button>
              <button
                onClick={() => setTagihanSubView('DETAIL')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  tagihanSubView === 'DETAIL'
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                <span>Rincian Item Tagihan ({tagihanFiltered.length})</span>
              </button>
            </div>

            {/* Live Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={
                  tagihanSubView === 'PER_KELAS'
                    ? 'Cari nama kelas / wali...'
                    : tagihanSubView === 'PER_SISWA'
                    ? 'Cari nama / NIS / kelas...'
                    : tagihanSubView === 'PER_WALI_KELAS'
                    ? 'Cari wali kelas / rombel...'
                    : 'Cari siswa / pos tagihan...'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-hidden focus:ring-1 focus:ring-sky-500 w-48 sm:w-64"
              />
            </div>
          </div>

          {/* VIEW 1: REKAP PER KELAS */}
          {tagihanSubView === 'PER_KELAS' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-3">Rombel / Kelas</th>
                      <th className="py-2.5 px-4">Wali Kelas</th>
                      <th className="py-2.5 px-3 text-center">Jml Siswa</th>
                      <th className="py-2.5 px-4 text-right">Target Tagihan</th>
                      <th className="py-2.5 px-4 text-right">Realisasi (Bayar)</th>
                      <th className="py-2.5 px-4 text-right">Sisa Piutang</th>
                      <th className="py-2.5 px-4 text-center">Ketercapaian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rekapTagihanPerKelas.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400">
                          Tidak ada data rekapitulasi kelas yang cocok.
                        </td>
                      </tr>
                    ) : (
                      rekapTagihanPerKelas.map((k, idx) => (
                        <tr key={k.kelasNama} className="hover:bg-sky-50/50">
                          <td className="py-2.5 px-3 text-center font-mono text-slate-500">{idx + 1}</td>
                          <td className="py-2.5 px-3 font-bold text-slate-900">{k.kelasNama}</td>
                          <td className="py-2.5 px-4 text-slate-700 font-medium">{k.waliKelas}</td>
                          <td className="py-2.5 px-3 text-center font-mono">{k.jumlahSiswa}</td>
                          <td className="py-2.5 px-4 text-right font-mono font-semibold text-slate-800">
                            Rp {k.totalTagihan.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-700">
                            Rp {k.totalTerbayar.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-rose-600">
                            Rp {k.totalSisa.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-slate-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-2 rounded-full ${
                                    k.persenLunas >= 80 ? 'bg-emerald-500' : k.persenLunas >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                  }`}
                                  style={{ width: `${Math.min(100, k.persenLunas)}%` }}
                                />
                              </div>
                              <span className="font-mono font-bold text-[11px] text-slate-700 w-9 text-right">
                                {k.persenLunas}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {rekapTagihanPerKelas.length > 0 && (
                    <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300">
                      <tr>
                        <td colSpan={3} className="py-2.5 px-4 text-slate-800">
                          TOTAL KESELURUHAN ({rekapTagihanPerKelas.length} Kelas)
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          {rekapTagihanPerKelas.reduce((sum, k) => sum + k.jumlahSiswa, 0)}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-slate-900">
                          Rp {rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTagihan, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-emerald-700">
                          Rp {rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTerbayar, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-rose-600">
                          Rp {rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalSisa, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-center font-mono text-sky-800">
                          {(() => {
                            const tot = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTagihan, 0);
                            const bay = rekapTagihanPerKelas.reduce((sum, k) => sum + k.totalTerbayar, 0);
                            return tot > 0 ? `${Math.round((bay / tot) * 100)}%` : '0%';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* VIEW 2: REKAP PER SISWA */}
          {tagihanSubView === 'PER_SISWA' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-3">NIS</th>
                      <th className="py-2.5 px-4">Nama Siswa</th>
                      <th className="py-2.5 px-3">Kelas</th>
                      <th className="py-2.5 px-3 text-center">Jml Tagihan</th>
                      <th className="py-2.5 px-4 text-right">Total Tagihan</th>
                      <th className="py-2.5 px-4 text-right">Terbayarkan</th>
                      <th className="py-2.5 px-4 text-right">Belum Terbayar</th>
                      <th className="py-2.5 px-3 text-center">Capaian</th>
                      <th className="py-2.5 px-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedRekapSiswa.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-8 text-center text-slate-400">
                          Tidak ada data rekapitulasi siswa yang cocok.
                        </td>
                      </tr>
                    ) : (
                      paginatedRekapSiswa.map((s, idx) => (
                        <tr key={s.siswaId} className="hover:bg-sky-50/50">
                          <td className="py-2 px-3 text-center font-mono text-slate-500">
                            {(tagihanSiswaPage - 1) * tagihanSiswaPerPage + idx + 1}
                          </td>
                          <td className="py-2 px-3 font-mono font-semibold text-slate-800">{s.nis}</td>
                          <td className="py-2 px-4 font-semibold text-slate-900">{s.namaSiswa}</td>
                          <td className="py-2 px-3 text-slate-600">{s.kelasNama}</td>
                          <td className="py-2 px-3 text-center font-mono text-slate-600">{s.jumlahTagihan}</td>
                          <td className="py-2 px-4 text-right font-mono font-medium text-slate-800">
                            Rp {s.totalTagihan.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-4 text-right font-mono font-bold text-emerald-700">
                            Rp {s.totalTerbayar.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-4 text-right font-mono font-bold text-rose-600">
                            Rp {s.totalSisa.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-3 text-center font-mono font-semibold text-slate-700">
                            {s.persenLunas}%
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                s.status === 'LUNAS'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : s.status === 'SEBAGIAN'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {s.status === 'LUNAS' ? 'Lunas' : s.status === 'SEBAGIAN' ? 'Cicilan / Sebagian' : 'Belum Bayar'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={tagihanSiswaPage}
                totalPages={totalSiswaRekapPages}
                totalRows={totalSiswaRekapRows}
                rowsPerPage={tagihanSiswaPerPage}
                onPageChange={setTagihanSiswaPage}
                onRowsPerPageChange={setTagihanSiswaPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          )}

          {/* VIEW 3: REKAP PER WALI KELAS */}
          {tagihanSubView === 'PER_WALI_KELAS' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-4">Nama Wali Kelas</th>
                      <th className="py-2.5 px-4">Rombel / Kelas yang Diampu</th>
                      <th className="py-2.5 px-3 text-center">Total Siswa</th>
                      <th className="py-2.5 px-4 text-right">Target Penagihan</th>
                      <th className="py-2.5 px-4 text-right">Realisasi (Bayar)</th>
                      <th className="py-2.5 px-4 text-right">Sisa Piutang Kelas</th>
                      <th className="py-2.5 px-4 text-center">Kinerja Capaian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rekapTagihanPerWaliKelas.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400">
                          Tidak ada data rekapitulasi wali kelas yang cocok.
                        </td>
                      </tr>
                    ) : (
                      rekapTagihanPerWaliKelas.map((w, idx) => (
                        <tr key={w.waliKelas} className="hover:bg-sky-50/50">
                          <td className="py-2.5 px-3 text-center font-mono text-slate-500">{idx + 1}</td>
                          <td className="py-2.5 px-4 font-bold text-slate-900">{w.waliKelas}</td>
                          <td className="py-2.5 px-4 text-slate-700 font-medium">
                            {w.kelasList.join(', ') || '-'}
                          </td>
                          <td className="py-2.5 px-3 text-center font-mono">{w.totalSiswa}</td>
                          <td className="py-2.5 px-4 text-right font-mono font-semibold text-slate-800">
                            Rp {w.totalTagihan.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-700">
                            Rp {w.totalTerbayar.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-rose-600">
                            Rp {w.totalSisa.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-slate-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-2 rounded-full ${
                                    w.persenLunas >= 80 ? 'bg-emerald-500' : w.persenLunas >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                  }`}
                                  style={{ width: `${Math.min(100, w.persenLunas)}%` }}
                                />
                              </div>
                              <span className="font-mono font-bold text-[11px] text-slate-700 w-9 text-right">
                                {w.persenLunas}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {rekapTagihanPerWaliKelas.length > 0 && (
                    <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300">
                      <tr>
                        <td colSpan={3} className="py-2.5 px-4 text-slate-800">
                          TOTAL SEMUA WALI KELAS ({rekapTagihanPerWaliKelas.length} Guru)
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          {rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalSiswa, 0)}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-slate-900">
                          Rp {rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTagihan, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-emerald-700">
                          Rp {rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTerbayar, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-rose-600">
                          Rp {rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalSisa, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-4 text-center font-mono text-sky-800">
                          {(() => {
                            const tot = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTagihan, 0);
                            const bay = rekapTagihanPerWaliKelas.reduce((sum, w) => sum + w.totalTerbayar, 0);
                            return tot > 0 ? `${Math.round((bay / tot) * 100)}%` : '0%';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* VIEW 4: DETAIL RINCIAN SEMUA ITEM */}
          {tagihanSubView === 'DETAIL' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-3">NIS</th>
                      <th className="py-2.5 px-4">Nama Siswa</th>
                      <th className="py-2.5 px-3">Kelas</th>
                      <th className="py-2.5 px-4">Pos Tagihan</th>
                      <th className="py-2.5 px-3 text-right">Tarif</th>
                      <th className="py-2.5 px-3 text-right">Potongan</th>
                      <th className="py-2.5 px-3 text-right">Bayar</th>
                      <th className="py-2.5 px-3 text-right">Sisa</th>
                      <th className="py-2.5 px-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedTagihan.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-8 text-center text-slate-400">
                          Tidak ada data tagihan yang cocok dengan filter yang dipilih.
                        </td>
                      </tr>
                    ) : (
                      paginatedTagihan.map((t, idx) => (
                        <tr key={t.id} className="hover:bg-sky-50/50">
                          <td className="py-2 px-3 text-center font-mono text-slate-500">
                            {(tagihanPage - 1) * tagihanPerPage + idx + 1}
                          </td>
                          <td className="py-2 px-3 font-mono font-semibold text-slate-800">{t.nis}</td>
                          <td className="py-2 px-4 font-semibold text-slate-900">{t.namaSiswa}</td>
                          <td className="py-2 px-3 text-slate-600">{t.kelas || t.kelasNama || ''}</td>
                          <td className="py-2 px-4 text-slate-800">
                            {t.namaPos} {t.bulan ? `- ${t.bulan}` : ''}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">{t.total.toLocaleString('id-ID')}</td>
                          <td className="py-2 px-3 text-right font-mono text-slate-500">
                            {t.potongan.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">
                            {t.bayar.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-rose-600">
                            {t.sisa.toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                t.status === 'PAID'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : t.status === 'PARTIAL'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {t.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={tagihanPage}
                totalPages={totalTagihanPages}
                totalRows={totalTagihanRows}
                rowsPerPage={tagihanPerPage}
                onPageChange={setTagihanPage}
                onRowsPerPageChange={setTagihanPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          )}
        </div>
      )}

      {/* TAB 2: REPORT PEMBAYARAN (Document Page 16) */}
      {activeReportType === 'Report Pembayaran' && (
        <div className="space-y-3">
          {/* SubView Mode Toggle */}
          <div className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-slate-200">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPembayaranSubView('DETAIL')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  pembayaranSubView === 'DETAIL'
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                <span>Daftar Transaksi Pembayaran ({pembayaranFiltered.length})</span>
              </button>
              <button
                onClick={() => setPembayaranSubView('REKAP_KASIR')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                  pembayaranSubView === 'REKAP_KASIR'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <BarChart2 className="w-3.5 h-3.5" />
                <span>Rekapitulasi per Petugas Kasir Loket ({rekapKasirList.length})</span>
              </button>
            </div>

            {filterKasir !== 'ALL' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  Difilter Kasir: <strong className="text-sky-800">{filterKasir}</strong>
                </span>
                <button
                  onClick={() => setFilterKasir('ALL')}
                  className="text-[11px] text-rose-600 hover:underline font-semibold"
                >
                  Reset Filter
                </button>
              </div>
            )}
          </div>

          {/* VIEW 1: REKAPITULASI PER PETUGAS KASIR LOKET */}
          {pembayaranSubView === 'REKAP_KASIR' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="p-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-700" />
                  <span className="text-xs font-bold text-emerald-900">
                    Rekapitulasi Kas Masuk per Petugas Kasir Loket SDMT
                  </span>
                </div>
                <span className="text-[11px] text-emerald-700 font-medium">
                  Klik "Detail Transaksi" pada petugas untuk memfilter mutasi kasirnya
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-4">Nama Petugas Kasir Loket / Kanal</th>
                      <th className="py-2.5 px-3 text-center">Jml Transaksi</th>
                      <th className="py-2.5 px-3 text-right">Penerimaan Tunai / Cash (Rp)</th>
                      <th className="py-2.5 px-3 text-right">Non-Tunai / Gateway (Rp)</th>
                      <th className="py-2.5 px-4 text-right">Total Penerimaan (Rp)</th>
                      <th className="py-2.5 px-3 text-center">Porsi (%)</th>
                      <th className="py-2.5 px-3 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rekapKasirList.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400">
                          Tidak ada data transaksi kasir pada periode ini.
                        </td>
                      </tr>
                    ) : (
                      rekapKasirList.map((r, idx) => {
                        const grandTotalRekap = rekapKasirList.reduce((sum, it) => sum + it.totalNominal, 0);
                        const pct = grandTotalRekap > 0 ? ((r.totalNominal / grandTotalRekap) * 100).toFixed(1) : '0';
                        const isCurrentStaff = currentUser && (currentUser.nama === r.kasir || currentUser.username === r.kasir);

                        return (
                          <tr key={r.kasir} className="hover:bg-slate-50">
                            <td className="py-3 px-3 text-center font-mono text-slate-500">{idx + 1}</td>
                            <td className="py-3 px-4 font-semibold text-slate-900">
                              <div className="flex items-center gap-2">
                                <span className="w-7 h-7 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center font-bold text-xs uppercase">
                                  {r.kasir.charAt(0)}
                                </span>
                                <div>
                                  <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                    <span>{r.kasir}</span>
                                    {isCurrentStaff && (
                                      <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.2 rounded-full">
                                        Anda
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-slate-400">Petugas Loket / Kanal</span>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center font-mono font-bold text-slate-700">
                              {r.totalTransaksi} trx
                            </td>
                            <td className="py-3 px-3 text-right font-mono font-bold text-emerald-700">
                              Rp {r.totalTunai.toLocaleString('id-ID')}
                            </td>
                            <td className="py-3 px-3 text-right font-mono font-bold text-indigo-700">
                              Rp {r.totalNonTunai.toLocaleString('id-ID')}
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-black text-slate-900 text-sm">
                              Rp {r.totalNominal.toLocaleString('id-ID')}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <span className="font-mono text-xs font-semibold text-slate-600">{pct}%</span>
                                <div className="w-12 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                  <div className="bg-sky-600 h-1.5 rounded-full" style={{ width: `${pct}%` }}></div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <button
                                onClick={() => {
                                  setFilterKasir(r.kasir);
                                  setPembayaranSubView('DETAIL');
                                  setPembayaranPage(1);
                                }}
                                className="px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded text-xs font-semibold transition border border-sky-200 cursor-pointer"
                                title="Lihat semua transaksi yang diproses kasir ini"
                              >
                                Lihat Detail
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {rekapKasirList.length > 0 && (
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-bold text-slate-900">
                      <tr>
                        <td colSpan={2} className="py-3 px-4 text-right uppercase text-xs">
                          Grand Total Seluruh Kasir:
                        </td>
                        <td className="py-3 px-3 text-center font-mono">
                          {rekapKasirList.reduce((s, it) => s + it.totalTransaksi, 0)} trx
                        </td>
                        <td className="py-3 px-3 text-right font-mono text-emerald-800">
                          Rp {rekapKasirList.reduce((s, it) => s + it.totalTunai, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-3 px-3 text-right font-mono text-indigo-800">
                          Rp {rekapKasirList.reduce((s, it) => s + it.totalNonTunai, 0).toLocaleString('id-ID')}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-slate-900 text-sm font-black">
                          Rp {rekapKasirList.reduce((s, it) => s + it.totalNominal, 0).toLocaleString('id-ID')}
                        </td>
                        <td colSpan={2} className="py-3 px-3 text-center text-xs text-slate-500">
                          100% Rekap Terverifikasi
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* VIEW 2: DAFTAR TRANSAKSI DETAIL */}
          {pembayaranSubView === 'DETAIL' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">No</th>
                      <th className="py-2.5 px-3">Tanggal</th>
                      <th className="py-2.5 px-3 font-mono">No. Transaksi</th>
                      <th className="py-2.5 px-3 font-mono">No. Kuitansi</th>
                      <th className="py-2.5 px-4">Nama Siswa</th>
                      <th className="py-2.5 px-3">Kelas</th>
                      <th className="py-2.5 px-3 text-right">Total Bayar (Rp)</th>
                      <th className="py-2.5 px-3">Channel</th>
                      <th className="py-2.5 px-3">Kasir / Petugas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedPembayaran.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-slate-400">
                          Tidak ada transaksi yang cocok dengan filter yang dipilih.
                        </td>
                      </tr>
                    ) : (
                      paginatedPembayaran.map((trx, idx) => (
                        <tr key={trx.id} className="hover:bg-sky-50/50">
                          <td className="py-2 px-3 text-center font-mono text-slate-500">
                            {(pembayaranPage - 1) * pembayaranPerPage + idx + 1}
                          </td>
                          <td className="py-2 px-3 text-slate-600">
                            {new Date(trx.tanggal).toLocaleString('id-ID', { hour12: false })}
                          </td>
                          <td className="py-2 px-3 font-mono font-bold text-slate-900">{trx.noTransaksi}</td>
                          <td className="py-2 px-3 font-mono text-sky-800 font-semibold">{trx.noKuitansi}</td>
                          <td className="py-2 px-4 font-semibold text-slate-900">{trx.namaSiswa}</td>
                          <td className="py-2 px-3 text-slate-600">{trx.kelas}</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">
                            {getPokokNominal(trx).toLocaleString('id-ID')}
                          </td>
                          <td className="py-2 px-3">
                            <span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] font-semibold">
                              {formatPaymentMethod(trx.channel, trx.metodeBayar)}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <span className="inline-flex items-center gap-1 font-semibold text-sky-900 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
                              <UserCheck className="w-3 h-3 text-sky-600" />
                              <span>{trx.kasir}</span>
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              <Pagination
                currentPage={pembayaranPage}
                totalPages={totalPembayaranPages}
                totalRows={pembayaranFiltered.length}
                rowsPerPage={pembayaranPerPage}
                onPageChange={setPembayaranPage}
                onRowsPerPageChange={setPembayaranPerPage}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </div>
          )}
        </div>
      )}

      {/* TAB 3: REPORT TUNGGAKAN (Document Page 17) */}
      {activeReportType === 'Report Tunggakan' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-3.5 bg-rose-50 border-b border-rose-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span className="text-xs font-bold text-rose-900">
                Daftar Tunggakan Siswa Belum Lunas ({tunggakanList.length} Tagihan)
              </span>
            </div>
            <span className="text-xs text-rose-700 font-medium">
              Kirim Notifikasi Pengingat Tagihan via WhatsApp Orang Tua
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3 w-12 text-center">No</th>
                  <th className="py-2.5 px-3">NIS</th>
                  <th className="py-2.5 px-4">Nama Siswa</th>
                  <th className="py-2.5 px-3">Kelas</th>
                  <th className="py-2.5 px-4">Tagihan Menunggak</th>
                  <th className="py-2.5 px-3 text-right">Nominal Tunggakan (Rp)</th>
                  <th className="py-2.5 px-4 text-center">Notifikasi WhatsApp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedTunggakan.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-400">
                      Tidak ada siswa yang menunggak pada filter kelas / tahun ajaran ini.
                    </td>
                  </tr>
                ) : (
                  paginatedTunggakan.map((t, idx) => {
                    const s = siswaList.find((x) => x.id === t.siswaId);
                    const rowNumber = (tunggakanPage - 1) * tunggakanPerPage + idx + 1;
                    return (
                      <tr key={t.id} className="hover:bg-rose-50/40">
                        <td className="py-2 px-3 text-center font-mono text-slate-500">{rowNumber}</td>
                        <td className="py-2 px-3 font-mono font-semibold text-slate-800">{t.nis}</td>
                        <td className="py-2 px-4 font-semibold text-slate-900">{t.namaSiswa}</td>
                        <td className="py-2 px-3 text-slate-600">{t.kelas || t.kelasNama || ''}</td>
                        <td className="py-2 px-4 font-medium text-slate-800">
                          {t.namaPos} {t.bulan ? `- ${t.bulan}` : ''}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-rose-600 text-sm">
                          Rp {t.sisa.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2 px-4 text-center">
                          <button
                            onClick={() =>
                              handleSendWaReminder(t.namaSiswa, s?.mobile || '081234567890', t.sisa)
                            }
                            className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold flex items-center gap-1.5 mx-auto transition shadow-xs cursor-pointer"
                          >
                            <Send className="w-3 h-3" />
                            <span>Kirim WA</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={tunggakanPage}
            totalPages={totalTunggakanPages}
            totalRows={totalTunggakanRows}
            rowsPerPage={tunggakanPerPage}
            onPageChange={setTunggakanPage}
            onRowsPerPageChange={setTunggakanPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </div>
      )}

      {/* TAB 4: REPORT PENERIMAAN (Document Page 18) */}
      {activeReportType === 'Report Penerimaan' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-bold text-sm text-slate-900">Rekapitulasi Penerimaan Kas per Pos Tagihan</h3>
            <p className="text-xs text-slate-500">Pemisahan alokasi dana rekening dan buku pembantu kas</p>
          </div>

          <div className="p-4 space-y-3">
            {posList.map((pos) => {
              const totalForPos = tagihanList
                .filter((t) => t.posId === pos.id)
                .reduce((sum, t) => sum + t.bayar, 0);

              return (
                <div
                  key={pos.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-sky-50/50 transition"
                >
                  <div>
                    <span className="font-mono font-bold text-sky-800 text-xs">{pos.kode}</span>
                    <h4 className="font-bold text-sm text-slate-900">{pos.namaPos}</h4>
                    <p className="text-xs text-slate-500 font-mono">Akun Penerimaan: {pos.akunPenerimaan}</p>
                  </div>

                  <div className="text-right">
                    <span className="text-xs text-slate-500 block">Total Diterima</span>
                    <span className="font-mono text-base font-black text-emerald-700">
                      Rp {totalForPos.toLocaleString('id-ID')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* TAB 5: REPORT TABUNGAN */}
      {activeReportType === 'Report Tabungan' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-sm text-slate-900">Rekapitulasi Saldo Tabungan Siswa</h3>
              <p className="text-xs text-slate-500">Laporan saldo akhir tabungan seluruh siswa per kelas</p>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3 w-12 text-center">No</th>
                  <th className="py-2.5 px-3">NIS</th>
                  <th className="py-2.5 px-4">Nama Siswa</th>
                  <th className="py-2.5 px-3">Kelas</th>
                  <th className="py-2.5 px-4 text-right">Saldo Akhir (Rp)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedTabungan.map((s, idx) => {
                  const rowNumber = (tabunganPage - 1) * tabunganPerPage + idx + 1;
                  return (
                    <tr key={s.id} className="hover:bg-sky-50/50 transition">
                      <td className="py-2 px-3 text-center text-slate-500 font-mono">{rowNumber}</td>
                      <td className="py-2 px-3 font-mono font-bold text-slate-800">{s.nis}</td>
                      <td className="py-2 px-4 font-semibold text-slate-900">{s.nama}</td>
                      <td className="py-2 px-3 text-slate-700">{s.kelasNama}</td>
                      <td className="py-2 px-4 text-right font-mono font-bold text-emerald-700 text-sm">
                        Rp {(s.saldoTabungan || 0).toLocaleString('id-ID')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={4} className="py-3 px-4 text-right font-bold text-slate-700">Total Saldo Tabungan:</td>
                  <td className="py-3 px-4 text-right font-mono font-black text-emerald-800 text-base">
                    Rp {tabunganFiltered
                        .reduce((sum, s) => sum + (s.saldoTabungan || 0), 0)
                        .toLocaleString('id-ID')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <Pagination
            currentPage={tabunganPage}
            totalPages={totalTabunganPages}
            totalRows={totalTabunganRows}
            rowsPerPage={tabunganPerPage}
            onPageChange={setTabunganPage}
            onRowsPerPageChange={setTabunganPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </div>
      )}
    </div>
  );
};
